// JOBE-0044.4: ChipRow — flex-wrap filter chip row. Mandatory scope
// chip, removable chips in insertion order, text-input add, trailing
// "Clear all", "Translated to query_entities" caption below.
//
// JOBE-0044.10: integrates FilterComposer. The composer is the primary
// chip-creation path (anchored to the `+ Add filter` button rendered
// inline). The ChipTextInput stays as a power-user secondary surface
// per the pinned coexistence decision. FilterChip body clicks open
// the matching value sub-popover anchored to the chip (edit path).

import { useCallback, useRef } from 'react';
import { FilterChip } from './FilterChip.jsx';
import { ChipTextInput } from './ChipTextInput.jsx';
import { TranslatedCaption } from './TranslatedCaption.jsx';
import { FRIENDLY_TOKENS, isReplaceChip } from './chipVocabulary.js';
import { FilterComposer } from './composer/FilterComposer.jsx';

export function ChipRow({ chips, onChange, roster }) {
  const hasRemovable = chips.some((c) => c.kind !== 'scope');
  const composerRef = useRef(null);
  const triggerRef = useRef(null);
  const chipRefs = useRef([]);

  const removeAt = (idx) => {
    if (chips[idx]?.kind === 'scope') return; // locked
    const next = chips.filter((_, i) => i !== idx);
    onChange(next);
  };

  const appendChip = useCallback((parsed) => {
    // Merge into an existing chip with the same friendlyToken — chips
    // are one-per-predicate per the design. Values dedupe.
    const existingIdx = chips.findIndex(
      (c) => c.kind === 'predicate' && c.friendlyToken === parsed.friendlyToken,
    );
    if (existingIdx >= 0) {
      // Date bounds (`since` / `until` / `created`) are replace-on-
      // append — merging `last-7d` with `2026-04-01` would produce an
      // incoherent `since=last-7d,2026-04-01`. Set chips merge values
      // and dedupe (multi-select OR semantics).
      const merged = isReplaceChip(parsed.friendlyToken)
        ? { ...chips[existingIdx], values: [...parsed.values] }
        : {
            ...chips[existingIdx],
            values: Array.from(new Set([...(chips[existingIdx].values || []), ...parsed.values])),
          };
      const next = chips.map((c, i) => (i === existingIdx ? merged : c));
      onChange(next);
      return;
    }
    const entry = FRIENDLY_TOKENS[parsed.friendlyToken];
    const chip = {
      kind: 'predicate',
      friendlyToken: parsed.friendlyToken,
      wirePredicate: parsed.wirePredicate || entry?.wirePredicate,
      values: parsed.values,
    };
    onChange([...chips, chip]);
  }, [chips, onChange]);

  const editChip = useCallback((index, nextChip) => {
    if (index < 0 || index >= chips.length) return;
    const merged = {
      ...chips[index],
      ...nextChip,
    };
    onChange(chips.map((c, i) => (i === index ? merged : c)));
  }, [chips, onChange]);

  const openComposerForEdit = useCallback((index) => {
    const chip = chips[index];
    if (!chip || chip.kind === 'scope') return;
    const anchor = chipRefs.current[index];
    composerRef.current?.openForEdit({
      chip,
      chipIndex: index,
      anchorRef: { current: anchor },
    });
  }, [chips]);

  const clearAll = () => {
    onChange(chips.filter((c) => c.kind === 'scope'));
  };

  // Derive active `type:` values for the composer's schema-fields
  // gating. The picker only enables Schema fields when exactly one
  // type is filtered.
  const activeTypeValues = (() => {
    const c = chips.find(
      (chip) => chip.kind === 'predicate' && chip.friendlyToken === 'type',
    );
    return c?.values || [];
  })();

  return (
    <div className="library-chip-row-wrap">
      <div className="library-chip-row" role="toolbar" aria-label="Filter chips">
        {chips.map((c, i) => (
          <FilterChip
            key={c.kind === 'scope' ? 'scope' : `${c.friendlyToken}:${i}`}
            ref={(el) => { chipRefs.current[i] = el; }}
            chip={c}
            onRemove={() => removeAt(i)}
            onEdit={c.kind === 'predicate' ? () => openComposerForEdit(i) : undefined}
          />
        ))}
        <button
          ref={triggerRef}
          type="button"
          className="library-chip-add-button"
          onClick={() => composerRef.current?.openPicker()}
          aria-label="Add filter"
        >
          + Add filter
        </button>
        <ChipTextInput onAppend={appendChip} />
        {hasRemovable && (
          <button
            type="button"
            className="library-chip-clear-all"
            onClick={clearAll}
          >
            Clear all
          </button>
        )}
      </div>
      <TranslatedCaption chips={chips} />
      <FilterComposer
        ref={composerRef}
        triggerRef={triggerRef}
        roster={roster}
        activeTypeValues={activeTypeValues}
        onAppendChip={appendChip}
        onEditChip={editChip}
      />
    </div>
  );
}
